/**
 * Multi-step walkthrough form to suggest a feed/seed store for approval.
 * Address required; phone, website, and email optional.
 * Search by store name or address (Mapbox Search Box via API) to pick a place and pre-fill.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { searchPlaces, reverseGeocode, parseGoogleMapsPlaceUrl } from '../../../services/geocodingService';
import { checkStoreListed, submitStore } from '../services/storeSubmissionService';
import { toolkitService } from '../services/toolkitService';
import { useToast } from '../../../components/ui/ToastProvider';
import { YInput } from '../../../ui/YInput';
import { YSelect } from '../../../ui/YSelect';
import { YButton } from '../../../ui/YButton';

export const SUGGEST_STORE_STEPS = [
  { id: 'location', title: 'Location' },
  { id: 'contact', title: 'Contact' },
  { id: 'review', title: 'Review' },
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];

/** Map full state name to 2-letter code so dropdown (value=AL) receives a matching option. */
const US_STATE_NAME_TO_CODE = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
  colorado: 'CO', connecticut: 'CT', delaware: 'DE', florida: 'FL', georgia: 'GA',
  hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA',
  kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
  massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS',
  missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV', 'new hampshire': 'NH',
  'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC',
  'north dakota': 'ND', ohio: 'OH', oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA',
  'rhode island': 'RI', 'south carolina': 'SC', 'south dakota': 'SD', tennessee: 'TN',
  texas: 'TX', utah: 'UT', vermont: 'VT', virginia: 'VA', washington: 'WA',
  'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY', 'district of columbia': 'DC',
};

function stateForDropdown(state) {
  if (!state || typeof state !== 'string') return '';
  const trimmed = state.trim();
  if (US_STATES.includes(trimmed)) return trimmed;
  const code = US_STATE_NAME_TO_CODE[trimmed.toLowerCase()];
  if (code) return code;
  return trimmed.length === 2 ? trimmed.toUpperCase() : trimmed;
}

const initialValues = {
  name: '',
  address: '',
  city: '',
  state: '',
  postal_code: '',
  latitude: '',
  longitude: '',
  phone: '',
  website: '',
  contact_email: '',
  notes: '',
};

function FormField({ id, label, hint, error, children }) {
  return (
    <div className="ss-form-field">
      <label className="ss-form-field__label" htmlFor={id}>
        {label}
      </label>
      {children}
      {hint ? <p className="ss-form-field__hint">{hint}</p> : null}
      {error ? (
        <p className="ss-form-field__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function SuggestStoreForm({ onSuccess, onCancel, renderStepper }) {
  const { show: showToast } = useToast();
  const [stepIndex, setStepIndex] = useState(0);
  const [values, setValues] = useState(initialValues);
  const [geocodeError, setGeocodeError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pasteUrl, setPasteUrl] = useState('');
  const [pasteLoading, setPasteLoading] = useState(false);
  const [pasteError, setPasteError] = useState('');
  const [storeSearchQuery, setStoreSearchQuery] = useState('');
  const [storeSuggestions, setStoreSuggestions] = useState([]);
  const [storeSuggestOpen, setStoreSuggestOpen] = useState(false);
  const [storeSuggestLoading, setStoreSuggestLoading] = useState(false);
  const [alreadyListed, setAlreadyListed] = useState(false);
  const storeSearchDebounceRef = useRef(null);
  const storeSearchContainerRef = useRef(null);

  useEffect(() => {
    const q = storeSearchQuery.trim();
    if (q.length < 2) {
      setStoreSuggestions([]);
      setStoreSuggestOpen(false);
      return;
    }
    if (storeSearchDebounceRef.current) clearTimeout(storeSearchDebounceRef.current);
    storeSearchDebounceRef.current = setTimeout(() => {
      storeSearchDebounceRef.current = null;
      setStoreSuggestLoading(true);
      toolkitService.placeSuggest(q, { limit: 8 })
        .then((data) => {
          setStoreSuggestions(data.suggestions || []);
          setStoreSuggestOpen(true);
        })
        .catch(() => setStoreSuggestions([]))
        .finally(() => setStoreSuggestLoading(false));
    }, 280);
    return () => {
      if (storeSearchDebounceRef.current) clearTimeout(storeSearchDebounceRef.current);
    };
  }, [storeSearchQuery]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (storeSearchContainerRef.current && !storeSearchContainerRef.current.contains(e.target)) {
        setStoreSuggestOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const set = useCallback((field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setGeocodeError('');
  }, []);

  const step = SUGGEST_STORE_STEPS[stepIndex];
  const isLast = stepIndex === SUGGEST_STORE_STEPS.length - 1;

  const canProceedLocation =
    values.name.trim() &&
    values.address.trim() &&
    values.city.trim() &&
    values.state.trim() &&
    values.postal_code.trim();

  const handleGeocodeAndNext = useCallback(async () => {
    if (!canProceedLocation) return;
    setGeocodeError('');
    const query = [values.address, values.city, values.state, values.postal_code]
      .filter(Boolean)
      .join(', ');
    const results = await searchPlaces(query, { limit: 1 });
    if (!results || results.length === 0) {
      setGeocodeError("We couldn't find that address. Check the address and try again.");
      return;
    }
    const place = results[0];
    setValues((prev) => ({
      ...prev,
      latitude: place.latitude,
      longitude: place.longitude,
    }));
    checkStoreListed(place.latitude, place.longitude)
      .then((d) => setAlreadyListed(!!d.already_listed))
      .catch(() => setAlreadyListed(false));
    setStepIndex(1);
  }, [canProceedLocation, values.address, values.city, values.state, values.postal_code]);

  const handleNext = useCallback(() => {
    if (step.id === 'location') {
      handleGeocodeAndNext();
      return;
    }
    if (step.id === 'contact') {
      setStepIndex(2);
    }
  }, [step.id, handleGeocodeAndNext]);

  const handleBack = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
    setGeocodeError('');
    setPasteError('');
  }, []);

  const handlePasteGoogleMapsUrl = useCallback(async () => {
    const parsed = parseGoogleMapsPlaceUrl(pasteUrl);
    if (!parsed || parsed.latitude == null || parsed.longitude == null) {
      setPasteError(
        'Paste a Google Maps link to a specific place (e.g. google.com/maps/place/Store+Name/...).'
      );
      return;
    }
    setPasteError('');
    setPasteLoading(true);
    try {
      const reversed = await reverseGeocode(parsed.longitude, parsed.latitude);
      if (!reversed) {
        setPasteError('Could not get address for that location.');
        return;
      }
      setValues((prev) => ({
        ...prev,
        name: parsed.name || prev.name || reversed.label || '',
        address: reversed.address || prev.address,
        city: reversed.city || prev.city,
        state: stateForDropdown(reversed.state) || prev.state,
        postal_code: reversed.postal_code || prev.postal_code,
        latitude: parsed.latitude,
        longitude: parsed.longitude,
      }));
      setPasteUrl('');
      setGeocodeError('');
      checkStoreListed(parsed.latitude, parsed.longitude)
        .then((d) => setAlreadyListed(!!d.already_listed))
        .catch(() => setAlreadyListed(false));
    } catch {
      setPasteError('Something went wrong. Try again.');
    } finally {
      setPasteLoading(false);
    }
  }, [pasteUrl]);

  const handleSubmit = useCallback(async () => {
    const rawWebsite = values.website.trim();
    const website =
      !rawWebsite ? '' : /^https?:\/\//i.test(rawWebsite) ? rawWebsite : `https://${rawWebsite}`;
    const payload = {
      name: values.name.trim(),
      address: values.address.trim(),
      city: values.city.trim(),
      state: values.state.trim(),
      postal_code: values.postal_code.trim(),
      latitude: Number(values.latitude),
      longitude: Number(values.longitude),
      phone: values.phone.trim() || '',
      website,
      contact_email: values.contact_email.trim() || '',
      notes: values.notes.trim() || '',
      inventory_tags: [],
    };
    setSubmitting(true);
    try {
      await submitStore(payload);
      showToast({
        title: 'Store submitted',
        body: 'Thanks! It will be reviewed and added once approved.',
        variant: 'success',
      });
      onSuccess?.();
    } catch (err) {
      const data = err.response?.data;
      let message = data?.detail ?? data?.message ?? err.message ?? 'Submission failed. Try again.';
      if (typeof message !== 'string' && data && typeof data === 'object') {
        const firstKey = Object.keys(data)[0];
        const firstVal = firstKey && data[firstKey];
        message = Array.isArray(firstVal)
          ? firstVal[0]
          : typeof firstVal === 'string'
            ? firstVal
            : JSON.stringify(data);
      }
      showToast({
        title: 'Could not submit',
        body: typeof message === 'string' ? message : 'Please check the form and try again.',
        variant: 'danger',
      });
    } finally {
      setSubmitting(false);
    }
  }, [values, showToast, onSuccess]);

  const selectSuggestion = (s) => {
    setValues((prev) => ({
      ...prev,
      name: s.name || prev.name,
      address: s.address || prev.address,
      city: s.city || prev.city,
      state: stateForDropdown(s.state) || prev.state,
      postal_code: s.postal_code || prev.postal_code,
      latitude: s.latitude,
      longitude: s.longitude,
    }));
    setStoreSuggestions([]);
    setStoreSearchQuery('');
    setStoreSuggestOpen(false);
    checkStoreListed(s.latitude, s.longitude)
      .then((d) => setAlreadyListed(!!d.already_listed))
      .catch(() => setAlreadyListed(false));
  };

  return (
    <>
      {renderStepper ? renderStepper(stepIndex) : null}

      {step.id === 'location' && (
        <div>
          <FormField
            id="ss-store-search"
            label="Search for the store"
            hint="Start typing a store name or street address to pre-fill the form."
          >
            <div ref={storeSearchContainerRef}>
              <YInput
                id="ss-store-search"
                type="text"
                value={storeSearchQuery}
                onChange={(e) => setStoreSearchQuery(e.target.value)}
                placeholder="Store name or address (e.g. Chambers Farm and Garden Supply)"
                autoComplete="off"
              />
              {storeSuggestLoading ? (
                <p className="ss-form-field__hint">Searching…</p>
              ) : null}
              {storeSuggestOpen && storeSuggestions.length > 0 ? (
                <ul className="ss-suggest-list">
                  {storeSuggestions.map((s, i) => (
                    <li key={i}>
                      <button
                        type="button"
                        className="ss-suggest-list__item"
                        onClick={() => selectSuggestion(s)}
                      >
                        <span className="ss-suggest-list__name">{s.name}</span>
                        {s.address ? (
                          <span className="ss-suggest-list__meta">
                            {[s.address, s.city, s.state].filter(Boolean).join(', ')}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </FormField>

          <FormField
            id="ss-paste-url"
            label="Or paste a Google Maps link"
            error={pasteError || undefined}
          >
            <div className="ss-paste-row">
              <YInput
                id="ss-paste-url"
                type="url"
                value={pasteUrl}
                onChange={(e) => {
                  setPasteUrl(e.target.value);
                  setPasteError('');
                }}
                placeholder="https://www.google.com/maps/place/Store+Name/..."
              />
              <YButton
                type="button"
                variant="secondary"
                size="sm"
                onClick={handlePasteGoogleMapsUrl}
                disabled={pasteLoading || !pasteUrl.trim()}
              >
                {pasteLoading ? '…' : 'Use link'}
              </YButton>
            </div>
          </FormField>

          <FormField id="ss-name" label="Store name *">
            <YInput
              id="ss-name"
              type="text"
              value={values.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Auburn Feed & Seed"
              required
            />
          </FormField>

          <FormField id="ss-address" label="Street address *">
            <YInput
              id="ss-address"
              type="text"
              value={values.address}
              onChange={(e) => set('address', e.target.value)}
              placeholder="123 Main St"
              required
            />
          </FormField>

          <div className="ss-form-row ss-form-row--location">
            <FormField id="ss-city" label="City *">
              <YInput
                id="ss-city"
                type="text"
                value={values.city}
                onChange={(e) => set('city', e.target.value)}
                placeholder="City"
                required
              />
            </FormField>
            <FormField id="ss-state" label="State *">
              <YSelect
                id="ss-state"
                value={values.state}
                onChange={(e) => set('state', e.target.value)}
                required
              >
                <option value="">State</option>
                {US_STATES.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </YSelect>
            </FormField>
            <FormField id="ss-zip" label="ZIP *">
              <YInput
                id="ss-zip"
                type="text"
                value={values.postal_code}
                onChange={(e) => set('postal_code', e.target.value)}
                placeholder="ZIP"
                required
              />
            </FormField>
          </div>
          {geocodeError ? (
            <p className="ss-form-field__error" role="alert">
              {geocodeError}
            </p>
          ) : null}
        </div>
      )}

      {step.id === 'contact' && (
        <div>
          <FormField id="ss-phone" label="Phone (optional)">
            <YInput
              id="ss-phone"
              type="tel"
              value={values.phone}
              onChange={(e) => set('phone', e.target.value)}
              placeholder="(555) 123-4567"
            />
          </FormField>
          <FormField id="ss-website" label="Website (optional)">
            <YInput
              id="ss-website"
              type="url"
              value={values.website}
              onChange={(e) => set('website', e.target.value)}
              placeholder="https://..."
            />
          </FormField>
          <FormField id="ss-email" label="Contact email (optional)">
            <YInput
              id="ss-email"
              type="email"
              value={values.contact_email}
              onChange={(e) => set('contact_email', e.target.value)}
              placeholder="store@example.com"
            />
          </FormField>
        </div>
      )}

      {step.id === 'review' && (
        <div>
          {alreadyListed ? (
            <div className="ss-alert ss-alert--info" role="status">
              This store is already approved and listed. You don&apos;t need to suggest it again.
            </div>
          ) : null}
          <div className="ss-review">
            <p className="ss-review__name">{values.name}</p>
            <p className="ss-review__line">
              {values.address}, {values.city}, {values.state} {values.postal_code}
            </p>
            {values.phone ? <p className="ss-review__line">{values.phone}</p> : null}
            {values.website ? <p className="ss-review__line">{values.website}</p> : null}
            {values.contact_email ? <p className="ss-review__line">{values.contact_email}</p> : null}
          </div>
          <FormField id="ss-notes" label="Notes (optional)">
            <YInput
              id="ss-notes"
              type="textarea"
              rows={3}
              value={values.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Anything else we should know"
            />
          </FormField>
        </div>
      )}

      <div className="ss-form-actions">
        <YButton type="button" variant="secondary" onClick={stepIndex === 0 ? onCancel : handleBack}>
          {stepIndex === 0 ? 'Cancel' : 'Back'}
        </YButton>
        {!isLast ? (
          <YButton
            type="button"
            variant="primary"
            onClick={step.id === 'location' ? handleGeocodeAndNext : handleNext}
            disabled={step.id === 'location' ? !canProceedLocation : false}
          >
            Next
          </YButton>
        ) : (
          <YButton
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={submitting || alreadyListed}
          >
            {submitting ? 'Submitting…' : alreadyListed ? 'Already listed' : 'Submit for approval'}
          </YButton>
        )}
      </div>
    </>
  );
}

export default SuggestStoreForm;
